import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  GetAdminLogDaysAPI,
  GetAdminLogSummaryAPI,
  GetAdminLogsAPI,
} from '../../../../api/AdminAPI';

const DEFAULT_LEVELS = ['debug', 'info', 'warning', 'error', 'critical'];
const DEFAULT_PAGINATION = { total: 0, page: 1, limit: 50, pages: 1 };
const DEFAULT_TOTALS = { debug: 0, info: 0, warning: 0, error: 0, critical: 0 };
const DEFAULT_SUMMARY = {
  exists: false,
  totals: DEFAULT_TOTALS,
  total: 0,
  by_channel: {},
  top_errors: [],
  security: [],
  security_total: 0,
  last_errors: [],
  invalid_lines: 0,
  php_log_lines: 0,
  previous_day: '',
  previous_totals: DEFAULT_TOTALS,
  previous_exists: false,
  folder: { bytes: 0, days: 0, oldest: '-' },
  errors_count: 0,
  warnings_count: 0,
};
const SEARCH_DEBOUNCE_MS = 400;
const AUTO_REFRESH_MS = 30000;
const LOGS_LIMIT = 50;

/** Data de hoje no fuso do navegador, usada só até days.php responder com a data do servidor. */
function localTodayStr() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * useAdminLogs
 *
 * Hook responsável pela tela de Logs do Admin Geral: dias disponíveis (days.php),
 * resumo do dia selecionado (summary.php) e lista paginada/filtrada (list.php).
 * Todos os filtros (data, canal, nível, empresa, req, busca, página) vivem na URL
 * via useSearchParams, para que o link possa ser compartilhado. Quando o dia
 * selecionado é hoje, a lista e o resumo são recarregados a cada 30s (pausável).
 *
 * @returns {Object} estado e ações da tela
 */
function useAdminLogs() {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParams = useCallback((updates, resetPage = true) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });
      if (resetPage) next.delete('page');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Dias disponíveis (days.php) ─────────────────────────────────────────
  const [days, setDays] = useState([]);
  const [levels, setLevels] = useState(DEFAULT_LEVELS);
  const [serverToday, setServerToday] = useState(localTodayStr());
  const [daysError, setDaysError] = useState(null);

  const fetchDays = useCallback(() => {
    GetAdminLogDaysAPI((response) => {
      if (!response || response.success === false) {
        setDaysError(response?.error || 'Erro ao carregar os dias disponíveis.');
        return;
      }
      setDaysError(null);
      setDays(response.days || []);
      if (response.levels && response.levels.length > 0) setLevels(response.levels);
      if (response.today) setServerToday(response.today);
    });
  }, []);

  useEffect(() => {
    fetchDays();
  }, [fetchDays]);

  const dayOptions = useMemo(() => {
    if (days.some((d) => d.date === serverToday)) return days;
    return [{ date: serverToday, channels: [], has_php_log: false, bytes: 0 }, ...days];
  }, [days, serverToday]);

  // ─── Filtros (na URL) ─────────────────────────────────────────────────────
  const date = searchParams.get('date') || serverToday;
  const channel = searchParams.get('channel') || '';
  const level = searchParams.get('level') || '';
  const company = searchParams.get('company') ?? '';
  const req = searchParams.get('req') || '';
  const q = searchParams.get('q') || '';
  const page = parseInt(searchParams.get('page'), 10) || 1;

  const isToday = date === serverToday;
  const selectedDayInfo = useMemo(() => dayOptions.find((d) => d.date === date) || null, [dayOptions, date]);
  const channels = selectedDayInfo?.channels || [];

  const setDate = useCallback((value) => updateParams({ date: value }), [updateParams]);
  const setChannel = useCallback((value) => updateParams({ channel: value }), [updateParams]);
  const setLevel = useCallback((value) => updateParams({ level: value }), [updateParams]);
  const setPage = useCallback((value) => updateParams({ page: value }, false), [updateParams]);

  // ─── Empresa / req / busca (draft local + debounce -> URL) ────────────────
  const [companyInput, setCompanyInputState] = useState(company);
  const [reqInput, setReqInputState] = useState(req);
  const [searchInput, setSearchInputState] = useState(q);
  const companyTimer = useRef(null);
  const reqTimer = useRef(null);
  const searchTimer = useRef(null);

  useEffect(() => { setCompanyInputState(company); }, [company]);
  useEffect(() => { setReqInputState(req); }, [req]);
  useEffect(() => { setSearchInputState(q); }, [q]);

  const setCompanyInput = useCallback((value) => {
    setCompanyInputState(value);
    if (companyTimer.current) clearTimeout(companyTimer.current);
    companyTimer.current = setTimeout(() => updateParams({ company: value }), SEARCH_DEBOUNCE_MS);
  }, [updateParams]);

  const setReqInput = useCallback((value) => {
    setReqInputState(value);
    if (reqTimer.current) clearTimeout(reqTimer.current);
    reqTimer.current = setTimeout(() => updateParams({ req: value }), SEARCH_DEBOUNCE_MS);
  }, [updateParams]);

  const setSearchInput = useCallback((value) => {
    setSearchInputState(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => updateParams({ q: value }), SEARCH_DEBOUNCE_MS);
  }, [updateParams]);

  useEffect(() => () => {
    if (companyTimer.current) clearTimeout(companyTimer.current);
    if (reqTimer.current) clearTimeout(reqTimer.current);
    if (searchTimer.current) clearTimeout(searchTimer.current);
  }, []);

  /** Aplica o filtro de empresa imediatamente (clique na coluna "Empresa" de uma linha). */
  const applyCompanyFilter = useCallback((value) => {
    if (companyTimer.current) clearTimeout(companyTimer.current);
    const strValue = String(value);
    setCompanyInputState(strValue);
    updateParams({ company: strValue });
  }, [updateParams]);

  /** Aplica o filtro de req imediatamente (clique no chip "Req" de uma linha). */
  const applyReqFilter = useCallback((value) => {
    if (reqTimer.current) clearTimeout(reqTimer.current);
    setReqInputState(value);
    updateParams({ req: value });
  }, [updateParams]);

  /** Aplica busca pela mensagem de um "top erro" e limpa o filtro de canal. */
  const applyTopErrorFilter = useCallback((msg) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setSearchInputState(msg);
    updateParams({ q: msg, channel: '' });
  }, [updateParams]);

  // ─── Resumo do dia (summary.php) ───────────────────────────────────────────
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  const fetchSummary = useCallback((silent = false) => {
    if (!silent) setSummaryLoading(true);
    setSummaryError(null);
    GetAdminLogSummaryAPI(date, (response) => {
      if (!response || response.success === false) {
        setSummaryError(response?.error || 'Erro ao carregar o resumo do dia. Tente novamente.');
        setSummaryLoading(false);
        return;
      }
      setSummary(response.summary || DEFAULT_SUMMARY);
      setSummaryLoading(false);
    });
  }, [date]);

  useEffect(() => {
    fetchSummary(false);
  }, [fetchSummary]);

  // ─── Lista de logs (list.php) ──────────────────────────────────────────────
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError] = useState(null);

  const fetchLogs = useCallback((silent = false) => {
    if (!silent) setLogsLoading(true);
    setLogsError(null);
    GetAdminLogsAPI(
      { date, channel, level, company, req, q, page, limit: LOGS_LIMIT },
      (response) => {
        if (!response || response.success === false) {
          setLogsError(response?.error || 'Erro ao carregar os logs. Tente novamente.');
          setLogsLoading(false);
          return;
        }
        setLogs(response.logs || []);
        setPagination(response.pagination || { ...DEFAULT_PAGINATION, page, limit: LOGS_LIMIT });
        setLogsLoading(false);
      }
    );
  }, [date, channel, level, company, req, q, page]);

  useEffect(() => {
    fetchLogs(false);
  }, [fetchLogs]);

  // ─── Auto-refresh (só no dia de hoje) ──────────────────────────────────────
  const [autoRefreshPaused, setAutoRefreshPaused] = useState(false);
  const toggleAutoRefresh = useCallback(() => setAutoRefreshPaused((prev) => !prev), []);

  useEffect(() => {
    if (!isToday || autoRefreshPaused) return undefined;
    // Recarrega em silêncio (sem spinner) para a tabela não piscar a cada 30s.
    const timer = setInterval(() => {
      fetchLogs(true);
      fetchSummary(true);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [isToday, autoRefreshPaused, fetchLogs, fetchSummary]);

  return {
    date,
    setDate,
    dayOptions,
    channels,
    levels,
    isToday,
    daysError,

    summary,
    summaryLoading,
    summaryError,

    logs,
    logsLoading,
    logsError,
    pagination,
    setPage,

    channel,
    setChannel,
    level,
    setLevel,
    companyInput,
    setCompanyInput,
    reqInput,
    setReqInput,
    searchInput,
    setSearchInput,
    applyReqFilter,
    applyCompanyFilter,
    applyTopErrorFilter,

    autoRefreshPaused,
    toggleAutoRefresh,

    refresh: () => { fetchDays(); fetchSummary(false); fetchLogs(false); },
  };
}

export default useAdminLogs;
